// src/pages/Users/Inscription/components/GroupChoiceCard.jsx

import React from "react";
import {
  FaCheck,
  FaInfoCircle,
  FaLock,
  FaMapMarkerAlt,
} from "react-icons/fa";

import {
  getColorMeta,
  getGroupQuota,
  normalizeGroupYears,
} from "../utils/inscriptionUtils";

export default function GroupChoiceCard({
  group,
  dojo,
  disabled,
  unavailableReason,
  onSelect,
  onOpenDetails,
}) {
  const color = getColorMeta(group.color_groupe);
  const quota = getGroupQuota(group);
  const years = normalizeGroupYears(group.annees_naissance);

  return (
    <article
      className={`relative overflow-hidden rounded-[1.5rem] border bg-white shadow-sm transition dark:bg-slate-900 ${
        disabled
          ? "border-slate-200 opacity-60 grayscale dark:border-slate-800"
          : `${color.border} hover:-translate-y-0.5 hover:shadow-lg`
      }`}
    >
      <div className={`absolute inset-y-0 left-0 w-2 ${color.solid}`} />

      <div className="p-4 pl-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-base font-bold leading-snug text-slate-950 dark:text-white">
              {group.nom || "Groupe"}
            </h3>

            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs font-bold">
              {group.discipline_groupe ? (
                <span className={`rounded-full px-2.5 py-1 ${color.light} ${color.text}`}>
                  {group.discipline_groupe}
                </span>
              ) : null}

              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                <FaMapMarkerAlt className="text-[10px]" />
                {dojo?.dojo_nom || "Lieu non renseigné"}
              </span>
            </div>
          </div>

          {disabled ? (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-slate-900/80 px-2.5 py-1 text-[11px] font-bold text-white">
              <FaLock />
              Fermé
            </span>
          ) : (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-bold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
              <FaCheck />
              Ouvert
            </span>
          )}
        </div>

        <div className="mt-4">
          <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
            Années de naissance
          </p>

          {years.length ? (
            <div className="flex flex-wrap gap-2">
              {years.slice(0, 6).map((year) => (
                <span
                  key={year}
                  className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-bold text-blue-700 dark:bg-blue-500/10 dark:text-blue-300"
                >
                  {year}
                </span>
              ))}

              {years.length > 6 ? (
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                  +{years.length - 6}
                </span>
              ) : null}
            </div>
          ) : (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Non renseigné
            </p>
          )}
        </div>

        {disabled ? (
          <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-bold text-slate-600 dark:border-slate-800 dark:bg-slate-950/70 dark:text-slate-300">
            {unavailableReason || "Inscription impossible"}
          </div>
        ) : quota.hasQuota ? (
          <div className="mt-4">
            <div className="mb-1 flex justify-between text-xs font-bold text-slate-500 dark:text-slate-400">
              <span>
                {quota.members} / {quota.quota} inscrit(s)
              </span>
              <span>{quota.percent}%</span>
            </div>

            <div className="h-1.5 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
              <div
                className={`h-full rounded-full ${
                  quota.isFull ? "bg-rose-500" : color.solid
                }`}
                style={{ width: `${quota.percent}%` }}
              />
            </div>
          </div>
        ) : null}

        <div className="mt-4 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={onOpenDetails}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaInfoCircle />
            Détails
          </button>

          <button
            type="button"
            onClick={onSelect}
            disabled={disabled}
            className={`inline-flex items-center justify-center gap-2 rounded-2xl px-3 py-2.5 text-sm font-bold transition ${
              disabled
                ? "cursor-not-allowed bg-slate-200 text-slate-500 dark:bg-slate-800 dark:text-slate-400"
                : "bg-[#013E8E] text-white hover:bg-[#012f6d] dark:bg-indigo-500 dark:hover:bg-indigo-400"
            }`}
          >
            {disabled ? (
              <>
                <FaLock />
                Fermé
              </>
            ) : (
              <>
                <FaCheck />
                Choisir
              </>
            )}
          </button>
        </div>
      </div>
    </article>
  );
}